import {
  type EvalRunDetail,
  EvalRunDetailSchema,
  type EvalRunPage,
  EvalRunPageSchema,
  EvalRunSchema,
  type ListEvalRunsQuery,
  type TriggerEvalRun,
} from '@sbc/domain-models/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const EVAL_RUNS_KEY = ['admin', 'eval-runs'];

function listKey(query: ListEvalRunsQuery) {
  return [...EVAL_RUNS_KEY, 'list', query] as const;
}

function detailKey(id: string) {
  return [...EVAL_RUNS_KEY, 'detail', id] as const;
}

export function useEvalRuns(query: ListEvalRunsQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  return useQuery<EvalRunPage>({
    queryKey: listKey(query),
    queryFn: () => apiGet(`/api/admin/eval-runs?${params.toString()}`, EvalRunPageSchema),
  });
}

export function useEvalRun(id: string) {
  return useQuery<EvalRunDetail>({
    queryKey: detailKey(id),
    queryFn: () => apiGet(`/api/admin/eval-runs/${id}`, EvalRunDetailSchema),
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 2000 : false),
  });
}

export function useTriggerEvalRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TriggerEvalRun) =>
      apiSend('POST', '/api/admin/eval-runs', input, EvalRunSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_RUNS_KEY }),
  });
}

export function useCancelEvalRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend('POST', `/api/admin/eval-runs/${id}/cancel`, {}, EvalRunSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: EVAL_RUNS_KEY }),
  });
}
